import React, { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";

import { useHistory, useLocation, useParams } from "react-router-dom";
import StackGrid from "react-stack-grid";
import noFeedItems from "images/no-feed-items.png";

import client, { makeGet } from "@culturehq/client";
import { Loader } from "@culturehq/components";

import { useGaTracking } from "app/hooks";
import { getName, isIntellia, Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import StoryThumbnail from "components/common/StoryThumbnail";
import LightboxStories from "components/common/gallery/LightboxStories";
import ErrorBoundary from "components/common/ErrorBoundary";
import StoryTrends from "components/landingPage/StoryTrends";
import StoryItemPlaceholder from "./settings/StoryItemPlaceholder";

const initialState = {
  appending: null,
  appendingImages: false,
  error: null,
  fetching: true,
  pagination: {},
  stories: [],
  seed: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case "notFetching":
      return { ...state, fetching: false };
    case "appendStart":
      return { ...state, appending: state.pagination.currentPage + 1, appendingImages: true };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        stories: [...state.stories, ...action.response.stories],
        seed: action.response.seed
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: action.response.pagination,
        stories: action.response.stories,
        seed: action.response.seed
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    case "setStories":
      return {
        ...state,
        fetching: false,
        stories: action.stories
      };
    default:
      return state;
  }
};

const useFetchStories = (
  dispatch, queryParams, organization, user, trendId, preview, seed, prefix
) => useEffect(
  () => {
    if (!preview) {
      let cancelled = false;
      dispatch({ type: "fetchStart" });

      client.listOrganizationLandingPagesStories({
        ...queryParams,
        organization,
        userId: user?.id,
        seed,
        trendId,
        route: prefix ? `/${prefix}` : ""
      })
        .then(response => {
          if (!cancelled) {
            dispatch({ type: "fetchSuccess", response });
          }
        })
        .catch(error => {
          if (!cancelled) {
            dispatch({ type: "fetchError", error });
          }
        });

      return () => {
        cancelled = true;
      };
    }

    dispatch({ type: "notFetching" });
    return undefined;
  },
  [dispatch, queryParams, organization, user, trendId, preview, seed, prefix]
);

const useAppendStories = (
  dispatch, appending, queryParams, organization, user, trendId, preview, seed, prefix
) => useEffect(
  () => {
    if (appending && !preview) {
      let cancelled = false;

      client.listOrganizationLandingPagesStories({
        ...queryParams,
        page: appending,
        organization,
        userId: user?.id,
        seed,
        trendId,
        route: prefix ? `/${prefix}` : ""
      })
        .then(response => {
          if (!cancelled) {
            dispatch({ type: "appendSuccess", response });
          }
        })
        .catch(error => {
          if (!cancelled) {
            dispatch({ type: "appendError", error });
          }
        });

      return () => {
        cancelled = true;
      };
    }

    return undefined;
  },
  [dispatch, appending, queryParams, organization, user, trendId, preview, seed, prefix]
);

const useScrollListen = (dispatch, feedRef, pagination, stories) => useEffect(
  () => {
    if (pagination.currentPage !== pagination.totalPages) {
      const onScroll = () => {
        const feedBottom = (feedRef.current?.offsetTop || 0) + feedRef.current?.offsetHeight;
        const windowBottom = window.pageYOffset + window.innerHeight;

        if ((feedBottom - windowBottom) < 100 && stories.length !== 0) {
          dispatch({ type: "appendStart" });
        }
      };

      document.addEventListener("scroll", onScroll);
      return () => document.removeEventListener("scroll", onScroll);
    }

    return undefined;
  },
  [dispatch, feedRef, pagination, stories]
);

const getColumnWidth = (storiesNumber, orientation) => {
  if (window.innerWidth >= 768 && storiesNumber !== 1 && orientation !== "portrait") {
    return "50%";
  }

  return "100%";
};

const useOrientationChange = () => {
  const [orientation, setOrientation] = useState(window.orientation);

  useEffect(() => {
    const handler = () => setOrientation(window.orientation);

    window.addEventListener("orientationchange", handler);

    return () => window.removeEventListener("orientationchange", handler);
  }, []);

  return orientation === 0 ? "portrait" : "landscape"; // for -90 (right) and 90 (left)
};


const LandingPageStories = ({
  hideCreator, insideIframe, organization, preview, user, usersIds,
  queryParams, children, stories: storiesState, organizationId, trendId, isEducational,
  orgName, color, selectedFilters = []
}) => {
  const feedRef = useRef();
  const history = useHistory();
  const location = useLocation();
  const { gaSessionId, gaClientId, trackEvent } = useGaTracking();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [activeStory, setActiveStory] = useState(undefined);
  const [columnWidth, setColumnWidth] = useState("100%");
  const [handle, setHandle] = useState();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoStartTime, setVideoStartTime] = useState(null);
  const { prefix, storyId } = useParams();
  const orientation = useOrientationChange();

  const finalStories = storiesState || state.stories;
  const hasTrackedPlayEventRef = useRef(false);
  const handleRef = useRef();
  handleRef.current = handle;

  useFetchStories(dispatch, queryParams, organization, user, trendId, preview, state.seed, prefix);
  useAppendStories(
    dispatch, state.appending, queryParams, organization, user, trendId, preview, state.seed, prefix
  );
  useScrollListen(dispatch, feedRef, state.pagination, state.stories);

  useEffect(
    () => {
      if ((!isIntellia(orgName)) || !insideIframe) {
        const trackData = (eventAction, storyId = undefined) => trackEvent({
          organizationId,
          storyId,
          eventAction,
          url: window.location.href,
          type: "culture_page"
        });
        // Only fire page_view if there is NO storyId (home page or closed story)
        if (gaSessionId && gaClientId && !storyId) {
          trackData("page_view");
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, gaClientId, gaSessionId, insideIframe, orgName, storyId]
  );

  useEffect(
    () => {
      hasTrackedPlayEventRef.current = false;
    }, [activeStory]
  );

  useEffect(
    () => {
      setColumnWidth(getColumnWidth(finalStories.length, orientation));
    }, [finalStories.length, orientation]
  );

  useEffect(() => {
    const updateSize = () => {
      setTimeout(() => {
        setColumnWidth(getColumnWidth(finalStories.length, orientation));
        dispatch({ type: "setStories", stories: finalStories });
      }, 100);
    };

    window.addEventListener("orientationchange", updateSize);

    return () => window.removeEventListener("orientationchange", updateSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientation, finalStories]);

  useEffect(
    () => {
      const trackData = (eventAction, storyId = undefined) => trackEvent({
        organizationId,
        storyId,
        eventAction,
        url: window.location.href,
        type: "culture_page"
      });

      const getStory = async () => {
        const { story } = await makeGet(`/organizations/landing_pages/stories/${storyId}`, {
          organization,
          route: prefix ? `/${prefix}` : ""
        });
        setActiveStory(story);
        setModalIsOpen(true);

        if ((!isIntellia(orgName) || !insideIframe) && gaClientId && gaSessionId) {
          trackData("open_story", story.id);
        }
      };

      if (orgName && storyId) getStory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgName, storyId, gaClientId, gaSessionId]
  );

  useLayoutEffect(() => {
    const updateSize = () => {
      setColumnWidth(getColumnWidth(finalStories.length));
    };
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [finalStories.length]);

  if (state.fetching) {
    return (
      <div className="StoryItemPlaceholder__Container">
        <div className="StoryItemPlaceholder__Left">
          <StoryItemPlaceholder />
          <StoryItemPlaceholder />
        </div>
        <div className="StoryItemPlaceholder__Right">
          <StoryItemPlaceholder />
        </div>
      </div>
    );
  }

  if (state.error) {
    return <ErrorMessage error={state.error} />;
  }

  const trackData = (eventAction, storyId = undefined, params = {}) => {
    if ((!isIntellia(orgName)) || !insideIframe) {
      trackEvent({
        organizationId,
        storyId,
        eventAction,
        url: window.location.href,
        type: "culture_page",
        ...params
      });
    }
  };

  const handleStoryChange = goToIndex => {
    let target = goToIndex;

    if (goToIndex === -1) {
      target = finalStories.length - 1;
    }

    if (goToIndex > finalStories.length - 1) {
      target = 0;
    }
    handleVideoStoryPause();

    trackData("open_story", finalStories[target].id);

    setActiveStory(finalStories[target]);
    setModalIsOpen(true);
  };

  const handleClose = () => {
    handleVideoStoryPause();
    setModalIsOpen(false);
    // if it is a story path /story/STORY_ID
    if (/story\/\d+$/.test(location.pathname)) history.push(prefix ? `/${prefix}` : "/");
  };

  const handleMouseOver = story => {
    setHandle(setTimeout(() => {
      trackData("over_story", story.id);
    }, 2000));
  };

  const handleMouseLeave = () => {
    if (handleRef.current) {
      clearTimeout(handle);
      setHandle(undefined);
    }
  };

  const handleVideoStoryPlay = () => {
    if (!hasTrackedPlayEventRef.current) {
      trackData("play_video_story", activeStory.id);
      hasTrackedPlayEventRef.current = true;
    }
    setIsVideoPlaying(true);
    setVideoStartTime(Date.now());
  };

  const handleVideoStoryPause = () => {
    if (isVideoPlaying && videoStartTime) {
      const duration = (Date.now() - videoStartTime) / 1000;
      trackData("play_duration_story_video", activeStory.id, { time: duration });
      setIsVideoPlaying(false);
      setVideoStartTime(null);
    }
  };

  return (
    <div className="Preview-Browser__Stories">
      {finalStories.length === 0 && (
        <div className="EmptyResultsTags EmptyResultsTags-feed">
          <p><Trans>No stories to show...</Trans></p>
        </div>
      )}
      {finalStories.length > 0 && (
        <>
          {children}
          {!trendId && !user && selectedFilters.length === 0 && (
            <StoryTrends
              color={color}
              organization={organization}
              orgName={orgName}
              preview={preview}
              queryParams={queryParams}
            />
          )}
          <div className="Stories__container" ref={feedRef}>
            {activeStory && (
              <LightboxStories
                activeStory={activeStory}
                currentUserAnswered
                insideIframe={insideIframe}
                landingPage
                landingPagePrefix={prefix}
                modalIsOpen={modalIsOpen}
                noActions
                onClose={handleClose}
                organization={organization}
                organizationId={organizationId}
                orgName={orgName}
                onStoryChange={handleStoryChange}
                onVideoStoryPlay={handleVideoStoryPlay}
                onVideoStoryPause={handleVideoStoryPause}
                preview={preview}
                showProfileLink={
                  user || !!usersIds?.find(userId => userId === activeStory.creator.id)
                }
                stories={finalStories}
              />
            )}
            <div className="Stories__container--images">
              <StackGrid
                columnWidth={columnWidth}
                monitorImagesLoaded
              >
                {finalStories.map((story, index) => (
                  <ErrorBoundary key={`${story.id}-${index}`}>
                    <StoryThumbnail
                      showCreator={!hideCreator}
                      index={index}
                      key={`${story.id}-${index}`}
                      story={story}
                      onClick={handleStoryChange}
                      showDefaultTag
                      showCreatorTitle
                      showQuestion
                      showActions={false}
                      showTags
                      landingPage
                      onOverStory={handleMouseOver}
                      onLeaveStory={handleMouseLeave}
                      organization={organization}
                      isEducational={isEducational}
                    />
                  </ErrorBoundary>
                ))}
              </StackGrid>
              {state.appending && (
                <Loader loading />
              )}
              {finalStories.length < 1 && (
                <div className="EmptyResults EmptyResults-feed">
                  <img src={noFeedItems} alt="Empty stories" />
                  <h2><Trans>Looks like no stories have been shared {user ? `by ${getName(user)}` : "for this yet"}...</Trans></h2>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LandingPageStories;
